"use client"

import type React from "react"
import { useState, useEffect, useMemo } from "react"
import { getDocs, collection, query, orderBy } from "firebase/firestore"
import { db } from "../firebase/config"
import {
  Trash2,
  Search,
  Users,
  Shield,
  UserPlus,
  Filter,
  Download,
  Mail,
  Calendar,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  UserCheck,
  Eye,
  ShoppingBag,
  MapPin,
  Phone,
  CreditCard,
  Package,
  ArrowLeft,
  TrendingUp,
} from "lucide-react"
import { userService } from "../services/userService"
import { ordersService } from "../services/firebase"
import LoadingSpinner from "../components/common/LoadingSpinner"
import toast from "react-hot-toast"
import { isAdminUser } from "../services/auth"

interface User {
  id: string
  name: string
  email: string
  role: "admin" | "user"
  createdAt: string
  lastLogin?: string
  phone?: string
  location?: string
  status?: "active" | "inactive"
  totalOrders?: number
  totalSpent?: number
  lastOrderDate?: string
}

interface UserOrder {
  id: string
  total: number
  status: string
  createdAt: string
  items: Array<{
    productId: string
    name: string
    quantity: number
    price: number
  }>
  shippingAddress?: {
    name: string
    address: string
    city: string
    state: string
    pincode: string
    phone: string
  }
  paymentMethod?: string
  paymentStatus?: string
}

interface PaginationInfo {
  currentPage: number
  totalPages: number
  hasNext: boolean
  hasPrev: boolean
}

const UserDetailsModal: React.FC<{
  user: User
  onClose: () => void
}> = ({ user, onClose }) => {
  const [orders, setOrders] = useState<UserOrder[]>([])
  const [isLoadingOrders, setIsLoadingOrders] = useState(true)
  const [activeTab, setActiveTab] = useState<"details" | "orders">("details")

  useEffect(() => {
    if (user.role === "user") {
      loadUserOrders()
    } else {
      setIsLoadingOrders(false)
    }
  }, [user.id, user.role])

  const loadUserOrders = async () => {
    try {
      setIsLoadingOrders(true)
      const allOrders = await ordersService.getOrders()
      const userOrders = allOrders
        .filter((order) => order.userId === user.id)
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

      setOrders(userOrders as unknown as UserOrder[])
    } catch (error) {
      console.error("Error loading user orders:", error)
    } finally {
      setIsLoadingOrders(false)
    }
  }

  const orderStats = useMemo(() => {
    const totalOrders = orders.length
    const totalSpent = orders.reduce((sum, order) => sum + order.total, 0)
    const avgOrderValue = totalOrders > 0 ? totalSpent / totalOrders : 0
    const lastOrderDate = orders.length > 0 ? orders[0].createdAt : null

    const statusCounts = orders.reduce(
      (acc, order) => {
        acc[order.status] = (acc[order.status] || 0) + 1
        return acc
      },
      {} as Record<string, number>,
    )

    return {
      totalOrders,
      totalSpent,
      avgOrderValue,
      lastOrderDate,
      statusCounts,
    }
  }, [orders])

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{user.name}</h2>
              <p className="text-gray-600">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                user.role === "admin"
                  ? "bg-blue-100 text-blue-800"
                  : user.status === "active"
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
              }`}
            >
              {user.role === "admin" ? "Admin" : user.status || "User"}
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab("details")}
            className={`px-6 py-3 font-medium text-sm ${
              activeTab === "details"
                ? "border-b-2 border-primary-500 text-primary-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            User Details
          </button>
          {user.role === "user" && (
            <button
              onClick={() => setActiveTab("orders")}
              className={`px-6 py-3 font-medium text-sm ${
                activeTab === "orders"
                  ? "border-b-2 border-primary-500 text-primary-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Orders & Purchases ({orderStats.totalOrders})
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {activeTab === "details" && (
            <div className="space-y-6">
              {/* User Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Contact Information</h3>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <Mail className="w-5 h-5 text-gray-400" />
                      <span className="text-gray-900">{user.email}</span>
                    </div>
                    {user.phone && (
                      <div className="flex items-center space-x-3">
                        <Phone className="w-5 h-5 text-gray-400" />
                        <span className="text-gray-900">{user.phone}</span>
                      </div>
                    )}
                    {user.location && (
                      <div className="flex items-center space-x-3">
                        <MapPin className="w-5 h-5 text-gray-400" />
                        <span className="text-gray-900">{user.location}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Account Information</h3>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <Calendar className="w-5 h-5 text-gray-400" />
                      <div>
                        <span className="text-sm text-gray-500">Joined:</span>
                        <span className="text-gray-900 ml-2">
                          {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : "Unknown"}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <UserCheck className="w-5 h-5 text-gray-400" />
                      <div>
                        <span className="text-sm text-gray-500">Last Login:</span>
                        <span className="text-gray-900 ml-2">
                          {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : "Never"}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Shield className="w-5 h-5 text-gray-400" />
                      <div>
                        <span className="text-sm text-gray-500">Role:</span>
                        <span className="text-gray-900 ml-2 capitalize">{user.role}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Purchase Summary - Only for customers */}
              {user.role === "user" && (
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Purchase Summary</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-primary-600">{orderStats.totalOrders}</div>
                      <div className="text-sm text-gray-500">Total Orders</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">₹{orderStats.totalSpent.toLocaleString()}</div>
                      <div className="text-sm text-gray-500">Total Spent</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        ₹{Math.round(orderStats.avgOrderValue).toLocaleString()}
                      </div>
                      <div className="text-sm text-gray-500">Avg Order Value</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "orders" && user.role === "user" && (
            <div className="space-y-6">
              {/* Order Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <ShoppingBag className="w-8 h-8 text-blue-600" />
                    <div>
                      <div className="text-2xl font-bold text-blue-600">{orderStats.totalOrders}</div>
                      <div className="text-sm text-blue-600">Total Orders</div>
                    </div>
                  </div>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <CreditCard className="w-8 h-8 text-green-600" />
                    <div>
                      <div className="text-2xl font-bold text-green-600">₹{orderStats.totalSpent.toLocaleString()}</div>
                      <div className="text-sm text-green-600">Total Spent</div>
                    </div>
                  </div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <TrendingUp className="w-8 h-8 text-purple-600" />
                    <div>
                      <div className="text-2xl font-bold text-purple-600">
                        ₹{Math.round(orderStats.avgOrderValue).toLocaleString()}
                      </div>
                      <div className="text-sm text-purple-600">Avg Order</div>
                    </div>
                  </div>
                </div>
                <div className="bg-orange-50 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <Package className="w-8 h-8 text-orange-600" />
                    <div>
                      <div className="text-2xl font-bold text-orange-600">
                        {orderStats.statusCounts["delivered"] || 0}
                      </div>
                      <div className="text-sm text-orange-600">Delivered</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Orders List */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Order History</h3>
                {isLoadingOrders ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                    <span className="ml-2 text-gray-600">Loading orders...</span>
                  </div>
                ) : orders.length === 0 ? (
                  <div className="text-center py-8">
                    <ShoppingBag className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No orders found for this customer</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {orders.map((order) => (
                      <div key={order.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-4">
                            <div>
                              <div className="font-medium text-gray-900">Order #{order.id.slice(-8)}</div>
                              <div className="text-sm text-gray-500">
                                {new Date(order.createdAt).toLocaleDateString()} at{" "}
                                {new Date(order.createdAt).toLocaleTimeString()}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-semibold text-gray-900">₹{order.total.toLocaleString()}</div>
                            <span
                              className={`inline-block px-2 py-1 text-xs rounded-full ${
                                order.status === "pending"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : order.status === "confirmed"
                                    ? "bg-blue-100 text-blue-800"
                                    : order.status === "shipped"
                                      ? "bg-purple-100 text-purple-800"
                                      : order.status === "delivered"
                                        ? "bg-green-100 text-green-800"
                                        : "bg-red-100 text-red-800"
                              }`}
                            >
                              {order.status}
                            </span>
                          </div>
                        </div>

                        {/* Order Items */}
                        <div className="border-t border-gray-100 pt-3">
                          <div className="text-sm text-gray-600 mb-2">Items ({order.items.length}):</div>
                          <div className="space-y-1">
                            {order.items.map((item, index) => (
                              <div key={index} className="flex justify-between text-sm">
                                <span className="text-gray-700">
                                  {item.name} × {item.quantity}
                                </span>
                                <span className="text-gray-900">₹{(item.price * item.quantity).toLocaleString()}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Shipping Address */}
                        {order.shippingAddress && (
                          <div className="border-t border-gray-100 pt-3 mt-3">
                            <div className="text-sm text-gray-600 mb-1">Shipping Address:</div>
                            <div className="text-sm text-gray-700">
                              {order.shippingAddress.name}, {order.shippingAddress.address},{" "}
                              {order.shippingAddress.city}, {order.shippingAddress.state} -{" "}
                              {order.shippingAddress.pincode}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const AdminCustomers: React.FC = () => {
  const [users, setUsers] = useState<User[]>([])
  const [filteredUsers, setFilteredUsers] = useState<User[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [roleFilter, setRoleFilter] = useState<"all" | "admin" | "user">("all")
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all")
  const [sortBy, setSortBy] = useState<"name" | "email" | "createdAt" | "totalSpent" | "role">("createdAt")
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc")
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize] = useState(10)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Create user form state
  const [createForm, setCreateForm] = useState({
    name: "",
    email: "",
    role: "user" as "admin" | "user",
    password: "",
  })

  // Statistics
  const stats = useMemo(() => {
    const total = users.length
    const customers = users.filter((u) => u.role === "user").length
    const admins = users.filter((u) => u.role === "admin").length
    const active = users.filter((u) => u.status === "active").length
    const totalRevenue = users.filter((u) => u.role === "user").reduce((sum, u) => sum + (u.totalSpent || 0), 0)

    return {
      total,
      customers,
      admins,
      active,
      totalRevenue,
    }
  }, [users])

  // Simplified load users function with better error handling
  const loadUsersSimple = async (reset = false) => {
    const loadingState = reset ? setIsLoading : setIsRefreshing
    loadingState(true)
    setError(null)

    try {
      console.log("🔄 Loading users (simplified)...")

      // First, just get all users without order data
      const q = query(collection(db, "users"), orderBy("createdAt", "desc"))

      const snapshot = await getDocs(q)
      const allUsers = snapshot.docs.map((doc) => ({
        id: doc.id,
        name: doc.data().name || "Unknown",
        email: doc.data().email || "Unknown",
        role: doc.data().role || "user",
        createdAt: doc.data().createdAt || new Date().toISOString(),
        lastLogin: doc.data().lastLogin,
        phone: doc.data().phone,
        location: doc.data().location,
        status: doc.data().status || "inactive",
        totalOrders: 0,
        totalSpent: 0,
        lastOrderDate: undefined,
      })) as User[]

      console.log(`✅ Loaded ${allUsers.length} users successfully`)
      setUsers(allUsers)

      // Now load order data in background for customers only
      await loadOrderDataInBackground(allUsers)
    } catch (err) {
      console.error("❌ Error loading users:", err)
      setError(`Failed to load users: ${err instanceof Error ? err.message : "Unknown error"}`)
      setUsers([])
    } finally {
      loadingState(false)
    }
  }

  // Load order data in background to avoid blocking UI
  const loadOrderDataInBackground = async (users: User[]) => {
    try {
      console.log("📊 Loading order data in background...")
      const customers = users.filter((user) => user.role === "user")

      if (customers.length === 0) {
        console.log("No customers found, skipping order data loading")
        return
      }

      // Load orders once
      const allOrders = await ordersService.getOrders()
      console.log(`📦 Loaded ${allOrders.length} total orders`)

      // Update each customer with their order stats
      const updatedUsers = users.map((user) => {
        if (user.role === "user") {
          const userOrders = allOrders.filter((order) => order.userId === user.id)
          const totalOrders = userOrders.length
          const totalSpent = userOrders.reduce((sum, order) => sum + order.total, 0)
          const lastOrderDate = userOrders.length > 0 ? userOrders[0].createdAt : undefined

          return {
            ...user,
            totalOrders,
            totalSpent,
            status: (totalOrders > 0 ? "active" : "inactive") as "active" | "inactive",
            lastOrderDate,
          }
        }
        return {
          ...user,
          status: "active" as const,
        }
      })

      console.log("✅ Updated users with order data")
      setUsers(updatedUsers)
    } catch (error) {
      console.error("❌ Error loading order data:", error)
      // Don't show error to user since basic user data is already loaded
    }
  }

  useEffect(() => {
    loadUsersSimple(true)
  }, [])

  useEffect(() => {
    filterUsers()
  }, [users, searchQuery, roleFilter, statusFilter])

  const filterUsers = () => {
    let filtered = [...users]

    if (searchQuery) {
      filtered = filtered.filter(
        (user) =>
          user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
          user.role.toLowerCase().includes(searchQuery.toLowerCase()) ||
          user.phone?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          user.location?.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    }

    if (roleFilter !== "all") {
      filtered = filtered.filter((user) => user.role === roleFilter)
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter((user) => user.status === statusFilter)
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal: any = a[sortBy]
      let bVal: any = b[sortBy]

      if (sortBy === "createdAt") {
        aVal = new Date(aVal || 0).getTime()
        bVal = new Date(bVal || 0).getTime()
      } else if (typeof aVal === "string") {
        aVal = aVal.toLowerCase()
        bVal = bVal?.toLowerCase() || ""
      }

      if (sortOrder === "asc") {
        return aVal > bVal ? 1 : -1
      } else {
        return aVal < bVal ? 1 : -1
      }
    })

    setFilteredUsers(filtered)
    setCurrentPage(1)
  }

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!createForm.name || !createForm.email || !createForm.password) {
      toast.error("Please fill in all fields")
      return
    }

    try {
      // Create user in Firebase Auth
      const { createUserWithEmailAndPassword } = await import("firebase/auth")
      const { auth } = await import("../firebase/config")

      const userCredential = await createUserWithEmailAndPassword(auth, createForm.email, createForm.password)

      // Create user document in Firestore
      await userService.initializeUserData(userCredential.user.uid, {
        name: createForm.name,
        email: createForm.email,
        role: createForm.role,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      })

      toast.success("User created successfully")
      setShowCreateModal(false)
      setCreateForm({ name: "", email: "", role: "user", password: "" })
      loadUsersSimple()
    } catch (error) {
      console.error("Error creating user:", error)
      toast.error("Failed to create user")
    }
  }

  const handleUpdateUserRole = async (userId: string, newRole: "admin" | "user") => {
    try {
      await userService.updateUserData(userId, { role: newRole })
      toast.success("User role updated successfully")
      loadUsersSimple()
    } catch (error) {
      console.error("Error updating user role:", error)
      toast.error("Failed to update user role")
    }
  }

  const handleDeleteUser = async (userId: string, userEmail: string) => {
    // Prevent deletion of the main admin
    if (isAdminUser(userEmail)) {
      toast.error("Cannot delete the main admin user")
      return
    }

    if (window.confirm(`Are you sure you want to delete user "${userEmail}"?`)) {
      try {
        await userService.deleteUser(userId)
        toast.success("User deleted successfully")
        loadUsersSimple()
      } catch (error) {
        console.error("Error deleting user:", error)
        toast.error("Failed to delete user")
      }
    }
  }

  const getRoleBadge = (role: string, email: string) => {
    const isMainAdmin = isAdminUser(email)
    if (isMainAdmin) {
      return <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">Main Admin</span>
    }
    return role === "admin" ? (
      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">Admin</span>
    ) : (
      <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-full">User</span>
    )
  }

  // Export to CSV
  const exportToCSV = () => {
    const headers = [
      "Name",
      "Email",
      "Role",
      "Phone",
      "Status",
      "Joined",
      "Last Login",
      "Total Orders",
      "Total Spent",
      "Location",
    ]
    const csvData = filteredUsers.map((user) => [
      user.name,
      user.email,
      user.role,
      user.phone || "",
      user.status || "",
      user.createdAt ? new Date(user.createdAt).toLocaleDateString() : "",
      user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : "",
      user.totalOrders || 0,
      user.totalSpent || 0,
      user.location || "",
    ])

    const csvContent = [headers, ...csvData].map((row) => row.map((field) => `"${field}"`).join(",")).join("\n")

    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `users-${new Date().toISOString().split("T")[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  // Handle sort
  const handleSort = (field: typeof sortBy) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))
    } else {
      setSortBy(field)
      setSortOrder("desc")
    }
  }

  // Pagination info
  const paginationInfo: PaginationInfo = useMemo(() => {
    const totalPages = Math.ceil(filteredUsers.length / pageSize)
    return {
      currentPage,
      totalPages,
      hasNext: currentPage < totalPages,
      hasPrev: currentPage > 1,
    }
  }, [filteredUsers.length, currentPage, pageSize])

  // Get current page users
  const currentUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize
    const endIndex = startIndex + pageSize
    return filteredUsers.slice(startIndex, endIndex)
  }, [filteredUsers, currentPage, pageSize])

  const SortableHeader: React.FC<{ field: typeof sortBy; children: React.ReactNode }> = ({ field, children }) => (
    <th
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center space-x-1">
        <span>{children}</span>
        {sortBy === field && <span className="text-primary-600">{sortOrder === "asc" ? "↑" : "↓"}</span>}
      </div>
    </th>
  )

  const StatCard: React.FC<{ icon: any; title: string; value: string | number; color: string }> = ({
    icon: Icon,
    title,
    value,
    color,
  }) => (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center">
        <div className={`p-3 rounded-full ${color}`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
        <div className="ml-4">
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="text-2xl font-semibold text-gray-900">{value}</p>
        </div>
      </div>
    </div>
  )

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* User Details Modal */}
      {selectedUser && <UserDetailsModal user={selectedUser} onClose={() => setSelectedUser(null)} />}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard icon={Users} title="Total Users" value={stats.total} color="bg-blue-500" />
        <StatCard icon={UserCheck} title="Customers" value={stats.customers} color="bg-green-500" />
        <StatCard icon={Shield} title="Admins" value={stats.admins} color="bg-purple-500" />
        <StatCard
          icon={CreditCard}
          title="Total Revenue"
          value={`₹${stats.totalRevenue.toLocaleString()}`}
          color="bg-orange-500"
        />
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User & Customer Management</h1>
          <p className="text-gray-600">Manage all users, customers, and admin access</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => loadUsersSimple(true)}
            disabled={isRefreshing}
            className="inline-flex items-center px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            onClick={exportToCSV}
            className="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
          >
            <UserPlus className="w-5 h-5 mr-2" /> Add User
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-lg shadow">
        {/* Filters */}
        <div className="p-6 bg-gray-50 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search by name, email, phone, or location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value as "all" | "admin" | "user")}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All Roles</option>
                  <option value="admin">Admins</option>
                  <option value="user">Customers</option>
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as "all" | "active" | "inactive")}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-6 bg-red-50 border-b border-red-200">
            <div className="text-red-800">{error}</div>
          </div>
        )}

        {/* Table */}
        {currentUsers.length === 0 ? (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchQuery || roleFilter !== "all" || statusFilter !== "all"
                ? "Try adjusting your filters or search terms."
                : "No users have been created yet."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <SortableHeader field="name">User</SortableHeader>
                  <SortableHeader field="role">Role</SortableHeader>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <SortableHeader field="createdAt">Joined</SortableHeader>
                  <SortableHeader field="totalSpent">Total Spent</SortableHeader>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Orders
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                          {user.role === "admin" ? (
                            <Shield className="w-5 h-5 text-primary-600" />
                          ) : (
                            <Users className="w-5 h-5 text-primary-600" />
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{user.name}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                          {user.phone && <div className="text-xs text-gray-400">{user.phone}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{getRoleBadge(user.role, user.email)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.role === "admin" ? (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          Admin
                        </span>
                      ) : (
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            user.status === "active"
                              ? "bg-green-100 text-green-800"
                              : user.status === "inactive"
                                ? "bg-red-100 text-red-800"
                                : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {user.status || "Unknown"}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {user.role === "user" ? `₹${(user.totalSpent || 0).toLocaleString()}` : "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.role === "user" ? user.totalOrders || 0 : "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setSelectedUser(user)}
                          className="text-primary-600 hover:text-primary-900 flex items-center space-x-1"
                        >
                          <Eye className="w-4 h-4" />
                          <span>View</span>
                        </button>
                        {!isAdminUser(user.email) && (
                          <>
                            <select
                              value={user.role}
                              onChange={(e) => handleUpdateUserRole(user.id, e.target.value as "admin" | "user")}
                              className="px-2 py-1 border border-gray-300 rounded text-xs"
                            >
                              <option value="user">User</option>
                              <option value="admin">Admin</option>
                            </select>
                            <button
                              onClick={() => handleDeleteUser(user.id, user.email)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {isAdminUser(user.email) && <span className="text-gray-400 text-xs">Main Admin</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {paginationInfo.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, filteredUsers.length)}{" "}
                of {filteredUsers.length} users
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage((prev) => prev - 1)}
                  disabled={!paginationInfo.hasPrev}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </button>
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, paginationInfo.totalPages) }, (_, i) => {
                    const pageNum = i + 1
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-2 text-sm font-medium rounded-md ${
                          currentPage === pageNum
                            ? "bg-primary-600 text-white"
                            : "text-gray-700 hover:bg-gray-50 border border-gray-300"
                        }`}
                      >
                        {pageNum}
                      </button>
                    )
                  })}
                </div>
                <button
                  onClick={() => setCurrentPage((prev) => prev + 1)}
                  disabled={!paginationInfo.hasNext}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-bold mb-4">Create New User</h2>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <input
                  type="password"
                  value={createForm.password}
                  onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  value={createForm.role}
                  onChange={(e) => setCreateForm({ ...createForm, role: e.target.value as "admin" | "user" })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
                  Create User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminCustomers
